import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import type { ComputedRef } from 'vue';
import {
    computed,
    onScopeDispose,
    ref,
    watch,
} from 'vue';
import type { ISingleTicket, ITicketError } from '@/interface/ITicket';
import { OpenTicketStatus, PriceChangeStatus } from '@/interface/enum';
import type {
    IOpenParlayResult,
    IOpenParlaySubBetResult,
} from '@/libraries/apis/ticketApi';
import { openParlayTicket as openParlayTicketApi } from '@/libraries/apis/ticketApi';
import { createKeyedTimers } from '@/libraries/betSlip/keyedTimers';
import {
    OpenTicketFailureAction,
    buildOpenTicketError,
    resolveOpenTicketFailure,
    resolveTicketChangeError,
} from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import { createPlaceTicketSnapshot } from '@/libraries/betSlip/ticketHelper';
import { useAutoClearError } from '@/libraries/composables/useAutoClearError';
import { resolvePriceChangeStatus } from '@/libraries/composables/usePriceChange';
import { getTotalOdds } from '@/libraries/helpers/comboParlayHelper';

export const PARLAY_SUB_BET_CHANGE_ERROR_DELAY_MS = 3000;

export const PARLAY_OPEN_TICKET_ERROR_DELAY_MS = 3000;

export interface IParlaySubBetSnapshot extends IOpenParlaySubBetResult {
    priceChangeStatus: PriceChangeStatus;
}

export interface IParlayOpenSnapshot {
    signature: string;
    uid: string;
    minBet: number;
    maxBet: number;
    subBets: ISingleTicket[];
    subBetResults: Record<string, IParlaySubBetSnapshot>;
    isComplete: boolean;
    isOddsChangeAccepted: boolean;
}

export enum ParlayOpenStatus {
    Empty = 'empty',
    Opening = 'opening',
    Ready = 'ready',
    Invalid = 'invalid',
    Error = 'error',
}

type ParlayOpenState =
    | { status: ParlayOpenStatus.Empty }
    | {
        status: ParlayOpenStatus.Opening;
        requestId: number;
        signature: string;
        previousSnapshot: IParlayOpenSnapshot | null;
    }
    | { status: ParlayOpenStatus.Ready, snapshot: IParlayOpenSnapshot }
    | { status: ParlayOpenStatus.Invalid, snapshot: IParlayOpenSnapshot }
    | {
        status: ParlayOpenStatus.Error;
        previousSnapshot: IParlayOpenSnapshot | null;
    };

export interface IParlayOpenTicketDeps {
    subBets: ComputedRef<ISingleTicket[]>;
    isActive: ComputedRef<boolean>;
    canOpen: ComputedRef<boolean>;
    setBalance: (balance: IOpenParlayResult['balance']) => void;
    markSubBetClosed: (key: string, messageKey: string) => void;
}

export function createParlaySubBetSignature(subBets: ISingleTicket[]): string {
    return subBets.map(ticket => ticket.key).sort((a, b) => a.localeCompare(b)).join('|');
}

function isBetLimitValid(result: IOpenParlayResult): boolean {
    return result.minBet > 0 && result.maxBet > 0 && result.maxBet >= result.minBet;
}

export function useParlayOpenTicket(deps: IParlayOpenTicketDeps) {
    const {
        subBets,
        isActive,
        canOpen,
        setBalance,
        markSubBetClosed,
    } = deps;
    const openState = ref<ParlayOpenState>({ status: ParlayOpenStatus.Empty });
    const {
        error: openTicketError,
        showTemporary: showTemporaryOpenTicketError,
        showPersistent: showPersistentOpenTicketError,
        clear: clearOpenTicketError,
    } = useAutoClearError<ITicketError>(PARLAY_OPEN_TICKET_ERROR_DELAY_MS);
    const subBetChangeErrors = ref<Record<string, ITicketError>>({});
    const lastKnownSubBetResults = ref<Record<string, IParlaySubBetSnapshot>>({});
    const subBetChangeErrorTimers = createKeyedTimers();
    let nextRequestId = 0;
    let cancelSource: CancelTokenSource | null = null;

    const displayedSnapshot = computed<IParlayOpenSnapshot | null>(() => {
        switch (openState.value.status) {
            case ParlayOpenStatus.Ready:
            case ParlayOpenStatus.Invalid:
                return openState.value.snapshot;
            case ParlayOpenStatus.Opening:
            case ParlayOpenStatus.Error:
                return openState.value.previousSnapshot;
            default:
                return null;
        }
    });
    const uid = computed(() => displayedSnapshot.value?.uid ?? '');
    const minBet = computed(() => displayedSnapshot.value?.minBet ?? 0);
    const maxBet = computed(() => displayedSnapshot.value?.maxBet ?? 0);
    const isOpening = computed(() => openState.value.status === ParlayOpenStatus.Opening);
    const isOpenBlocked = computed(() => (
        openTicketError.value !== null
        || openState.value.status === ParlayOpenStatus.Invalid
    ));
    const subBetResults = computed<Record<string, IParlaySubBetSnapshot>>(() => (
        displayedSnapshot.value?.subBetResults ?? {}
    ));
    const subBetErrors = computed<Record<string, OpenTicketStatus>>(() => Object.fromEntries(
        Object.values(subBetResults.value)
            .filter(subBet => !subBet.isSuccess)
            .map(subBet => [subBet.key, subBet.errorStatus]),
    ));
    const totalOdds = computed(() => {
        const snapshot = displayedSnapshot.value;
        if (!snapshot?.isComplete) {
            return 0;
        }
        const prices = snapshot.subBets.map(ticket => snapshot.subBetResults[ticket.key].price);
        return getTotalOdds(prices);
    });
    const isOddsChangeAccepted = computed(() => (
        displayedSnapshot.value?.isOddsChangeAccepted ?? true
    ));

    // A SubBet that leaves the selection must lose its retained price for good:
    // re-selecting the same Odds has to wait for a fresh quote instead of
    // reviving the price from the membership it just left.
    watch(subBets, forgetLastKnownSubBetResultsOutsideSelection);

    function cancelOpenTicket(reason: string) {
        cancelSource?.cancel(reason);
        cancelSource = null;
    }

    function resetOpenState() {
        cancelOpenTicket('Parlay in Single mode open reset');
        openState.value = { status: ParlayOpenStatus.Empty };
        clearOpenTicketError();
        clearSubBetChangeErrors();
    }

    function failOpenTicket(
        previousSnapshot: IParlayOpenSnapshot | null,
        errorStatus?: OpenTicketStatus,
    ) {
        const { messageKey, action } = resolveOpenTicketFailure(errorStatus);
        clearSubBetChangeErrors();
        const showError = action === OpenTicketFailureAction.ShowPersistentError
            ? showPersistentOpenTicketError
            : showTemporaryOpenTicketError;
        showError(buildOpenTicketError(messageKey, errorStatus));
        openState.value = {
            status: ParlayOpenStatus.Error,
            previousSnapshot,
        };
    }

    async function openTicket(acceptOddsChanges = false) {
        if (!canOpen.value) {
            return;
        }
        const requestSubBets = subBets.value
            .map(ticket => createPlaceTicketSnapshot(ticket) as ISingleTicket);
        const signature = createParlaySubBetSignature(requestSubBets);
        const requestId = ++nextRequestId;
        const candidateSnapshot = displayedSnapshot.value;
        // An incomplete snapshot (a SubBet the server could not quote) still
        // carries the last shown quote and prices: keeping it avoids blanking the footer
        // during the re-open and keeps price-change detection across it.
        // isParlayPlaceable independently rejects incomplete snapshots.
        const previousSnapshot = candidateSnapshot?.signature === signature
            ? candidateSnapshot
            : null;
        if (candidateSnapshot?.signature !== signature) {
            clearOpenTicketError();
            clearSubBetChangeErrors();
        }
        cancelOpenTicket('Superseded Parlay in Single mode openTicket');
        const requestCancelSource = axios.CancelToken.source();
        cancelSource = requestCancelSource;
        openState.value = {
            status: ParlayOpenStatus.Opening,
            requestId,
            signature,
            previousSnapshot,
        };
        try {
            const result = await openParlayTicketApi(requestSubBets, requestCancelSource.token);
            applyOpenTicketResult(result, {
                requestId,
                signature,
                subBets: requestSubBets,
                previousSnapshot,
                acceptOddsChanges,
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            console.error('Failed to open Parlay in Single mode Ticket', error);
            if (isCurrentRequest(requestId, signature)) {
                failOpenTicket(previousSnapshot);
            }
        } finally {
            if (cancelSource === requestCancelSource) {
                cancelSource = null;
            }
        }
    }

    async function acceptOddsChanges() {
        await openTicket(true);
    }

    function isCurrentRequest(requestId: number, signature: string): boolean {
        const state = openState.value;
        return state.status === ParlayOpenStatus.Opening
            && state.requestId === requestId
            && state.signature === signature
            && isActive.value
            && signature === createParlaySubBetSignature(subBets.value);
    }

    interface IApplyOpenTicketResultContext {
        requestId: number;
        signature: string;
        subBets: ISingleTicket[];
        previousSnapshot: IParlayOpenSnapshot | null;
        acceptOddsChanges: boolean;
    }

    function applyOpenTicketResult(
        result: IOpenParlayResult,
        context: IApplyOpenTicketResultContext,
    ) {
        const {
            requestId,
            signature,
            subBets: requestSubBets,
            previousSnapshot,
            acceptOddsChanges,
        } = context;
        if (!isCurrentRequest(requestId, signature)) {
            return;
        }
        if (!result.isSuccess) {
            failOpenTicket(previousSnapshot, result.errorStatus);
            return;
        }
        if (!isBetLimitValid(result)) {
            failOpenTicket(previousSnapshot, OpenTicketStatus.MaxOddsExceed);
            return;
        }
        clearOpenTicketError();
        setBalance(result.balance);
        const nextSubBetResults = Object.fromEntries(
            result.subBetResults.map(subBet => [subBet.key, subBet]),
        );
        result.subBetResults.forEach((subBet) => {
            if (subBet.isSuccess) {
                return;
            }
            const failure = resolveOpenTicketFailure(subBet.errorStatus);
            if (failure.action === OpenTicketFailureAction.Remove) {
                markSubBetClosed(subBet.key, failure.messageKey);
            }
        });
        const subBetKeys = new Set(result.subBetResults.map(subBet => subBet.key));
        const isComplete = result.subBetResults.length === requestSubBets.length
            && subBetKeys.size === requestSubBets.length
            && requestSubBets.every(ticket => nextSubBetResults[ticket.key]?.isSuccess === true);
        const isSameSelection = previousSnapshot?.signature === signature;
        const detectedPriceChangeStatuses = Object.fromEntries(requestSubBets.map((ticket) => {
            const nextSubBet = nextSubBetResults[ticket.key];
            const previousSubBet = isSameSelection
                ? previousSnapshot?.subBetResults[ticket.key]
                : undefined;
            const status = nextSubBet?.isSuccess === true && previousSubBet?.isSuccess === true
                ? resolvePriceChangeStatus(
                    { value: nextSubBet.price, priceStyle: nextSubBet.priceStyle },
                    { value: previousSubBet.price, priceStyle: previousSubBet.priceStyle },
                )
                : PriceChangeStatus.Remain;
            return [ticket.key, status];
        }));
        const hasPriceChange = Object.values(detectedPriceChangeStatuses)
            .some(status => status !== PriceChangeStatus.Remain);
        const detectedSubBetChangeErrors = requestSubBets.reduce<Record<string, ITicketError>>(
            (errors, ticket) => {
                const nextSubBet = nextSubBetResults[ticket.key];
                const previousSubBet = isSameSelection
                    ? previousSnapshot?.subBetResults[ticket.key]
                    : undefined;
                if (nextSubBet?.isSuccess !== true || previousSubBet?.isSuccess !== true) {
                    return errors;
                }
                const error = resolveTicketChangeError(
                    {
                        point: previousSubBet.point,
                        homeScore: previousSubBet.score.home,
                        awayScore: previousSubBet.score.away,
                    },
                    {
                        point: nextSubBet.point,
                        homeScore: nextSubBet.score.home,
                        awayScore: nextSubBet.score.away,
                    },
                );
                if (error !== null) {
                    errors[ticket.key] = error;
                }
                return errors;
            },
            {},
        );
        replaceSubBetChangeErrors(detectedSubBetChangeErrors);
        const nextSubBetSnapshots: Record<string, IParlaySubBetSnapshot> = Object.fromEntries(
            Object.entries(nextSubBetResults).map(([key, result]) => {
                const detectedStatus = detectedPriceChangeStatuses[key];
                const previousStatus = !acceptOddsChanges
                    && previousSnapshot?.isOddsChangeAccepted === false
                    ? previousSnapshot.subBetResults[key]?.priceChangeStatus
                    : PriceChangeStatus.Remain;
                return [key, {
                    ...result,
                    priceChangeStatus: detectedStatus !== PriceChangeStatus.Remain
                        ? detectedStatus
                        : previousStatus ?? PriceChangeStatus.Remain,
                }];
            }),
        );
        recordLastKnownSubBetResults(nextSubBetSnapshots);
        const snapshot: IParlayOpenSnapshot = {
            signature,
            uid: result.uid,
            minBet: result.minBet,
            maxBet: result.maxBet,
            subBets: requestSubBets,
            subBetResults: nextSubBetSnapshots,
            isComplete,
            isOddsChangeAccepted: acceptOddsChanges
                || !isSameSelection
                || (previousSnapshot?.isOddsChangeAccepted === true && !hasPriceChange),
        };
        openState.value = isComplete
            ? { status: ParlayOpenStatus.Ready, snapshot }
            : { status: ParlayOpenStatus.Invalid, snapshot };
    }

    function recordLastKnownSubBetResults(subBetSnapshots: Record<string, IParlaySubBetSnapshot>) {
        lastKnownSubBetResults.value = {
            ...lastKnownSubBetResults.value,
            ...Object.fromEntries(
                Object.entries(subBetSnapshots)
                    .filter(([, subBet]) => subBet.isSuccess),
            ),
        };
    }

    function forgetLastKnownSubBetResultsOutsideSelection(currentSubBets: ISingleTicket[]) {
        const activeKeys = new Set(currentSubBets.map(ticket => ticket.key));
        lastKnownSubBetResults.value = Object.fromEntries(
            Object.entries(lastKnownSubBetResults.value)
                .filter(([key]) => activeKeys.has(key)),
        );
    }

    function removeSubBetChangeError(key: string) {
        const nextErrors = { ...subBetChangeErrors.value };
        delete nextErrors[key];
        subBetChangeErrors.value = nextErrors;
    }

    function scheduleSubBetChangeErrorRemoval(key: string) {
        subBetChangeErrorTimers.schedule(
            key,
            PARLAY_SUB_BET_CHANGE_ERROR_DELAY_MS,
            () => removeSubBetChangeError(key),
        );
    }

    function replaceSubBetChangeErrors(errors: Record<string, ITicketError>) {
        subBetChangeErrorTimers.clearAll();
        subBetChangeErrors.value = errors;
        Object.keys(errors).forEach(scheduleSubBetChangeErrorRemoval);
    }

    function clearSubBetChangeErrors() {
        subBetChangeErrorTimers.clearAll();
        subBetChangeErrors.value = {};
    }

    onScopeDispose(() => {
        cancelOpenTicket('Parlay in Single mode scope disposed');
        subBetChangeErrorTimers.clearAll();
    });

    return {
        openTicketError,
        displayedSnapshot,
        uid,
        minBet,
        maxBet,
        isOpening,
        isOpenBlocked,
        subBetResults,
        lastKnownSubBetResults,
        subBetErrors,
        subBetChangeErrors,
        totalOdds,
        isOddsChangeAccepted,
        openTicket,
        acceptOddsChanges,
        resetOpenState,
    };
}
